import type { Chapter } from '../api/types';

export interface PlayerTrack {
  index: number;
  startOffset: number;
  duration: number;
  url: string;
  mimeType?: string;
  isHls?: boolean;
}

/** Finds the track that contains `time` (seconds from the start of the book). */
export function locateTrack(tracks: PlayerTrack[], time: number): { trackIndex: number; offset: number } {
  if (tracks.length === 0) return { trackIndex: 0, offset: 0 };
  const t = Math.max(0, time);
  for (let i = tracks.length - 1; i >= 0; i--) {
    const track = tracks[i]!;
    if (t >= track.startOffset - 0.001) {
      return { trackIndex: i, offset: Math.min(t - track.startOffset, Math.max(0, track.duration - 0.05)) };
    }
  }
  return { trackIndex: 0, offset: 0 };
}

export function chapterIndexAt(chapters: Chapter[], time: number): number {
  if (chapters.length === 0) return -1;
  for (let i = chapters.length - 1; i >= 0; i--) {
    if (time >= chapters[i]!.start - 0.25) return i;
  }
  return 0;
}

/** Synthesises one chapter per file when a book has no chapter markers. */
export function chaptersFromTracks(tracks: Array<{ startOffset: number; duration: number; title?: string }>): Chapter[] {
  if (tracks.length < 2) return [];
  return tracks.map((t, i) => ({ id: i, start: t.startOffset, end: t.startOffset + t.duration, title: t.title || `Part ${i + 1}` }));
}

/** Media types this browser can play, for ABS's direct-play negotiation. */
export function supportedMimeTypes(audio: HTMLMediaElement = document.createElement('audio')): string[] {
  const candidates = [
    'audio/mpeg',
    'audio/mp4',
    'audio/x-m4a',
    'audio/x-m4b',
    'audio/aac',
    'audio/flac',
    'audio/x-flac',
    'audio/ogg',
    'audio/opus',
    'audio/webm',
    'audio/wav',
    'audio/x-wav',
  ];
  const supported = candidates.filter((t) => audio.canPlayType(t) !== '');
  // m4b is an m4a container; browsers just don't advertise the alias.
  if (supported.includes('audio/mp4') && !supported.includes('audio/x-m4b')) supported.push('audio/x-m4b');
  if (supported.includes('audio/mp4') && !supported.includes('audio/x-m4a')) supported.push('audio/x-m4a');
  return supported;
}

export function smartRewindSeconds(pausedForMs: number): number {
  if (pausedForMs > 60 * 60 * 1000) return 30;
  if (pausedForMs > 5 * 60 * 1000) return 10;
  if (pausedForMs > 60 * 1000) return 3;
  return 0;
}
